import { EnqueueResultState, QueueIt } from "react-native-queue-it";

import {
  deleteExpoSecureStoreItemAsync,
  getExpoSecureStoreItemAsync,
  setExpoSecureStoreItemAsync,
} from "@whoosh/utilities/src/ExpoSecureStore/expoSecureStore";

export type NativeQueueItState =
  | "disabled"
  | "exited"
  | "passed"
  | "unavailable";

const QUEUE_IT_ADMISSIONS_STORAGE_KEY = "whoosh-queue-it-admissions-v1";
const NATIVE_QUEUE_IT_ADMISSION_TTL_MS = 30 * 60 * 1000;

type QueueItAdmission = Readonly<{
  admittedAt: number;
}>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseStoredAdmissions = (
  value: string | null
): Record<string, QueueItAdmission> => {
  if (!value) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(value);

    if (!isRecord(parsed)) {
      return {};
    }

    const admissions: Record<string, QueueItAdmission> = {};

    for (const [key, admission] of Object.entries(parsed)) {
      if (
        isRecord(admission) &&
        typeof admission.admittedAt === "number" &&
        Number.isFinite(admission.admittedAt)
      ) {
        admissions[key] = { admittedAt: admission.admittedAt };
      }
    }

    return admissions;
  } catch {
    return {};
  }
};

const admissionKey = (customerId: string, eventAlias: string): string =>
  JSON.stringify([customerId, eventAlias]);

const hasActiveStoredQueueItAdmission = async (
  customerId: string,
  eventAlias: string
): Promise<boolean> => {
  try {
    const admissions = parseStoredAdmissions(
      await getExpoSecureStoreItemAsync(QUEUE_IT_ADMISSIONS_STORAGE_KEY)
    );
    const admittedAt =
      admissions[admissionKey(customerId, eventAlias)]?.admittedAt;
    const now = Date.now();

    return (
      admittedAt !== undefined &&
      admittedAt <= now &&
      now - admittedAt < NATIVE_QUEUE_IT_ADMISSION_TTL_MS
    );
  } catch {
    return false;
  }
};

const persistQueueItAdmission = async (
  customerId: string,
  eventAlias: string
): Promise<void> => {
  try {
    const admissions = parseStoredAdmissions(
      await getExpoSecureStoreItemAsync(QUEUE_IT_ADMISSIONS_STORAGE_KEY)
    );

    admissions[admissionKey(customerId, eventAlias)] = {
      admittedAt: Date.now(),
    };
    await setExpoSecureStoreItemAsync(
      QUEUE_IT_ADMISSIONS_STORAGE_KEY,
      JSON.stringify(admissions)
    );
  } catch {
    // The cache is best-effort and must not change Queue-it admission.
  }
};

const clearStoredQueueItAdmission = async (
  customerId: string,
  eventAlias: string
): Promise<void> => {
  try {
    const admissions = parseStoredAdmissions(
      await getExpoSecureStoreItemAsync(QUEUE_IT_ADMISSIONS_STORAGE_KEY)
    );

    delete admissions[admissionKey(customerId, eventAlias)];

    if (Object.keys(admissions).length === 0) {
      await deleteExpoSecureStoreItemAsync(QUEUE_IT_ADMISSIONS_STORAGE_KEY);
      return;
    }

    await setExpoSecureStoreItemAsync(
      QUEUE_IT_ADMISSIONS_STORAGE_KEY,
      JSON.stringify(admissions)
    );
  } catch {
    // The cache is best-effort and must not change Queue-it admission.
  }
};

const runNativeQueueItAttempt = async (
  customerId: string,
  eventAlias: string
): Promise<Exclude<NativeQueueItState, "exited">> => {
  try {
    while (true) {
      const result = await QueueIt.run(customerId, eventAlias);

      if (result.State === EnqueueResultState.RestartedSession) {
        continue;
      }

      if (result.State === EnqueueResultState.Passed) {
        return "passed";
      }

      if (result.State === EnqueueResultState.Disabled) {
        return "disabled";
      }

      return "unavailable";
    }
  } catch {
    // The browser connector is fail-open; keep the native behavior aligned.
  }

  return "unavailable";
};

export const runNativeQueueIt = async (
  customerId: string,
  eventAlias: string
): Promise<NativeQueueItState> => {
  try {
    if (await hasActiveStoredQueueItAdmission(customerId, eventAlias)) {
      return "passed";
    }

    let webViewClosedSubscription: ReturnType<typeof QueueIt.on> | undefined;
    let resolveWebViewClosed: (state: NativeQueueItState) => void = () => {};
    const webViewClosed = new Promise<NativeQueueItState>((resolve) => {
      resolveWebViewClosed = resolve;
    });
    webViewClosedSubscription = QueueIt.on("webViewClosed", () => {
      resolveWebViewClosed("exited");
    });

    try {
      const result = await Promise.race([
        runNativeQueueItAttempt(customerId, eventAlias),
        webViewClosed,
      ]);

      if (result === "passed") {
        await persistQueueItAdmission(customerId, eventAlias);
      } else if (result === "disabled") {
        await clearStoredQueueItAdmission(customerId, eventAlias);
      }

      return result;
    } finally {
      webViewClosedSubscription?.remove();
    }
  } catch {
    // A missing or incompatible native module must not leave the gate pending.
    return "unavailable";
  }
};
